import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { paymentsAPI, referralsAPI, nmiAPI } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckCircle2, Sparkles, Zap, Repeat, BadgePercent, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const COLLECT_SCRIPT_URL = "https://secure.nmi.com/token/Collect.js";

const PricingModal = ({ open, onOpenChange, defaultPlan = null }) => {
    const [pricing, setPricing] = useState({
        one_time_formatted: "…",
        monthly_formatted: "…",
    });
    const [processingPlan, setProcessingPlan] = useState(null);
    const [referral, setReferral] = useState(null);
    const [collectReady, setCollectReady] = useState(false);
    const [nmiConfig, setNmiConfig] = useState(null);
    const [step, setStep] = useState("plans"); // "plans" | "billing"
    const [billing, setBilling] = useState(() => {
        try { return JSON.parse(localStorage.getItem("nmi_customer_billing") || "{}"); }
        catch { return {}; }
    });
    const billingRef = useRef(billing);
    const selectedPlanRef = useRef(null);
    const navigate = useNavigate();
    const { refreshUser } = useAuth();

    useEffect(() => { billingRef.current = billing; }, [billing]);
    const updateBilling = (k, v) => {
        setBilling(prev => {
            const next = { ...prev, [k]: v };
            try { localStorage.setItem("nmi_customer_billing", JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    // Reset step whenever the modal reopens
    useEffect(() => { if (open) setStep("plans"); }, [open]);

    const finalizePayment = useCallback(async (plan, paymentToken) => {
        try {
            const b = billingRef.current || {};
            const response = plan === "monthly"
                ? await nmiAPI.subscribe(paymentToken, b)
                : await nmiAPI.charge(paymentToken, b);
            const data = response.data;
            if (data.success) {
                toast.success(plan === "monthly" ? "Subscription active! Full access unlocked." : "Payment successful! Full access unlocked.");
                await refreshUser();
                onOpenChange(false);
                navigate("/letters");
            } else {
                toast.error(data.message || "Payment declined. Please try another card.");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Payment could not be processed. Please try again.");
        } finally {
            setProcessingPlan(null);
        }
    }, [refreshUser, onOpenChange, navigate]);

    // Load NMI Collect.js script + config once the modal opens
    useEffect(() => {
        if (!open) return;

        // Fetch prices and referral discount
        paymentsAPI.getPricing().then(r => setPricing(r.data)).catch(() => {});
        referralsAPI.me().then(r => setReferral(r.data)).catch(() => setReferral(null));

        let cancelled = false;

        const setupCollect = () => {
            if (!window.CollectJS || cancelled) return;
            try {
                window.CollectJS.configure({
                    variant: "lightbox",
                    styleSniffer: true,
                    country: "US",
                    currency: "USD",
                    callback: (tokenResponse) => {
                        const plan = selectedPlanRef.current;
                        if (!tokenResponse?.token) {
                            toast.error("Card tokenization failed. Please try again.");
                            setProcessingPlan(null);
                            return;
                        }
                        finalizePayment(plan, tokenResponse.token);
                    },
                    timeoutCallback: () => {
                        toast.error("Card entry timed out. Please try again.");
                        setProcessingPlan(null);
                    },
                    fieldsAvailableCallback: () => {
                        if (!cancelled) setCollectReady(true);
                    },
                });
                setCollectReady(true);
            } catch (e) {
                toast.error("Failed to initialize secure card entry.");
            }
        };

        nmiAPI.getConfig().then(({ data }) => {
            if (cancelled) return;
            if (!data.configured || !data.public_key) {
                toast.error("Payments are not yet configured. Please contact support.");
                return;
            }
            setNmiConfig(data);

            // Inject Collect.js with the tokenization key
            const existing = document.querySelector(`script[src="${COLLECT_SCRIPT_URL}"]`);
            if (existing) {
                if (window.CollectJS) setupCollect();
                else existing.addEventListener("load", setupCollect);
                return;
            }
            const script = document.createElement("script");
            script.src = COLLECT_SCRIPT_URL;
            script.setAttribute("data-tokenization-key", data.public_key);
            script.async = true;
            script.onload = setupCollect;
            script.onerror = () => toast.error("Could not load secure card form.");
            document.head.appendChild(script);
        }).catch(() => {
            toast.error("Could not reach payments service.");
        });

        return () => { cancelled = true; };
    }, [open, finalizePayment]);

    const pct = referral?.has_pending_discount ? referral.discount_percent : 0;
    const fmtDiscounted = (orig) => {
        const m = String(orig || "").match(/([\d.]+)/);
        if (!m || pct <= 0) return null;
        const n = parseFloat(m[1]);
        if (Number.isNaN(n)) return null;
        const discounted = Math.max(n * (1 - pct / 100), 0.50);
        return discounted % 1 === 0 ? `$${discounted.toFixed(0)}` : `$${discounted.toFixed(2)}`;
    };

    const handleChoose = (plan) => {
        if (!nmiConfig?.configured) {
            toast.error("Payments not configured.");
            return;
        }
        if (plan === "monthly" && !nmiConfig?.monthly_plan_id) {
            toast.error("Subscription plan not set up yet. Please contact support.");
            return;
        }
        selectedPlanRef.current = plan;
        setStep("billing");
    };

    const handleConfirmBilling = () => {
        if (!collectReady || !window.CollectJS) {
            toast.error("Payment form still loading. Please wait a moment and try again.");
            return;
        }
        if (!billing.address1 || !billing.zip) {
            toast.error("Street address and ZIP are required by our payment processor.");
            return;
        }
        const plan = selectedPlanRef.current;
        setProcessingPlan(plan);
        // Close our Radix Dialog before opening Collect.js — Radix's focus trap
        // and pointer-events lock will otherwise block the NMI lightbox inputs.
        onOpenChange(false);
        setTimeout(() => {
            try {
                window.CollectJS.startPaymentRequest();
            } catch (e) {
                toast.error("Could not open card entry. Please refresh and try again.");
                setProcessingPlan(null);
            }
        }, 150);
    };

    const Plan = ({ id, title, price, sub, highlight, perks, icon: Icon, cta }) => {
        const discountedPrice = fmtDiscounted(price);
        return (
        <div
            className={`flex flex-col rounded-2xl border p-6 transition-all ${
                highlight
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-border bg-background hover:border-foreground/30"
            }`}
            data-testid={`plan-${id}`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="font-serif text-lg font-semibold">{title}</h3>
                </div>
                {highlight && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                        Most Flexible
                    </span>
                )}
            </div>
            <div className="mb-4">
                {discountedPrice ? (
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold font-mono text-accent" data-testid={`plan-${id}-price-discounted`}>{discountedPrice}</span>
                        <span className="text-base text-muted-foreground line-through font-mono">{price}</span>
                        <span className="text-sm text-muted-foreground ml-1">{sub}</span>
                    </div>
                ) : (
                    <>
                        <span className="text-4xl font-bold font-mono">{price}</span>
                        <span className="text-sm text-muted-foreground ml-1">{sub}</span>
                    </>
                )}
            </div>
            <ul className="space-y-2 mb-6 flex-1">
                {perks.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{p}</span>
                    </li>
                ))}
            </ul>
            <Button
                className="w-full rounded-full btn-press"
                onClick={() => handleChoose(id)}
                disabled={processingPlan !== null}
                data-testid={`choose-${id}-btn`}
                variant={highlight ? "default" : "outline"}
            >
                {processingPlan === id ? (
                    <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                        Processing…
                    </span>
                ) : (
                    cta
                )}
            </Button>
        </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                data-testid="pricing-modal"
            >
                {step === "plans" ? (
                    <>
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Pick Your Plan</DialogTitle>
                    <DialogDescription>
                        Both plans unlock full dispute letters, AI action plans, and live tracking.
                    </DialogDescription>
                </DialogHeader>

                {pct > 0 && (
                    <div
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/30 text-sm"
                        data-testid="pricing-discount-applied"
                    >
                        <BadgePercent className="h-4 w-4 text-accent flex-shrink-0" />
                        <span className="text-foreground">
                            Your <strong>{pct}% referral discount</strong> is applied to checkout.
                        </span>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-4 pt-2">
                    <Plan
                        id="one_time"
                        title="One-Time Full Access"
                        price={pricing.one_time_formatted}
                        sub="paid once"
                        icon={Zap}
                        highlight={defaultPlan !== "monthly"}
                        cta={(
                            <span className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4" />
                                Get Full Access
                            </span>
                        )}
                        perks={[
                            "Unlock every dispute letter",
                            "Full AI-generated action plan",
                            "Lifetime access to current analysis",
                            "Dispute tracker with AI next steps",
                        ]}
                    />
                    <Plan
                        id="monthly"
                        title="Monthly Subscription"
                        price={pricing.monthly_formatted}
                        sub="/ month, cancel anytime"
                        icon={Repeat}
                        highlight={defaultPlan === "monthly"}
                        cta={(
                            <span className="flex items-center gap-2">
                                <Repeat className="h-4 w-4" />
                                Start Monthly Plan
                            </span>
                        )}
                        perks={[
                            "Everything in Full Access",
                            "Re-analyze new credit reports anytime",
                            "Ongoing SMS dispute alerts",
                            "Cancel in one click — no fees",
                        ]}
                    />
                </div>
                    </>
                ) : (
                    <div className="space-y-4" data-testid="pricing-billing-step">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                                <button
                                    onClick={() => setStep("plans")}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    aria-label="Back to plans"
                                    data-testid="pricing-back-btn"
                                >
                                    <ArrowLeft className="h-5 w-5" />
                                </button>
                                Billing address
                            </DialogTitle>
                            <DialogDescription>
                                Required by our card processor. This must match the address on your card, or the transaction will be declined.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2 space-y-1.5">
                                <Label htmlFor="bill-addr1">Street address</Label>
                                <Input
                                    id="bill-addr1"
                                    placeholder="123 Main St"
                                    value={billing.address1 || ""}
                                    onChange={(e) => updateBilling("address1", e.target.value)}
                                    data-testid="pricing-bill-address1"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="bill-city">City</Label>
                                <Input
                                    id="bill-city"
                                    placeholder="Dallas"
                                    value={billing.city || ""}
                                    onChange={(e) => updateBilling("city", e.target.value)}
                                    data-testid="pricing-bill-city"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="bill-state">State</Label>
                                    <Input
                                        id="bill-state"
                                        placeholder="TX"
                                        maxLength={2}
                                        value={billing.state || ""}
                                        onChange={(e) => updateBilling("state", e.target.value.toUpperCase())}
                                        data-testid="pricing-bill-state"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="bill-zip">ZIP</Label>
                                    <Input
                                        id="bill-zip"
                                        placeholder="75001"
                                        value={billing.zip || ""}
                                        onChange={(e) => updateBilling("zip", e.target.value)}
                                        data-testid="pricing-bill-zip"
                                    />
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-full rounded-full btn-press"
                            onClick={handleConfirmBilling}
                            disabled={processingPlan !== null || !collectReady}
                            data-testid="pricing-continue-to-card-btn"
                        >
                            {processingPlan ? (
                                <span className="flex items-center gap-2">
                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                                    Opening secure card entry…
                                </span>
                            ) : !collectReady ? (
                                <span className="flex items-center gap-2 opacity-70">
                                    <span className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />
                                    Preparing secure form…
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Lock className="h-4 w-4" />
                                    Continue to card entry
                                </span>
                            )}
                        </Button>
                    </div>
                )}

                <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1.5">
                    <Lock className="h-3 w-3" />
                    Secure card entry powered by NMI. We never see or store your card number.
                </p>
            </DialogContent>
        </Dialog>
    );
};

export default PricingModal;
